import { computed, inject } from '@angular/core';
import { Filter } from '@iot-platform/models/common';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FilterCategoryV2, FilterConfigurationV2, FilterEngineMode, FilterFieldV2 } from '../models';
import { FilterConfigurationService, FilterEngineSettingsService } from '../services';

type FilterEngineState = {
  // Filter state
  appliedFilters: Filter[];
  maxFilters: number;

  // Configuration state
  configuration: FilterConfigurationV2 | null;
  isLoadingConfiguration: boolean;
  configurationError: string | null;

  // UI state
  disabled: boolean;
  expanded: boolean;
  filterMode: FilterEngineMode;
  closeOnClickOutside: boolean;
  autoApply: boolean;
  multipleOperatorSupport: boolean;
  suppressOperator: boolean;

  // Selection state
  selectedCategory: FilterCategoryV2 | null;
  selectedField: FilterFieldV2 | null;

  // Filter criteria button
  filterCriteriaButtonTitle: string;

  // Menu coordination state
  currentOpenMenuId: string | null; // Track which menu is currently open
};

const FILTER_CRITERIA_BUTTON_DEFAULT_TITLE = 'SELECT FILTER CRITERIA';

const initialState: FilterEngineState = {
  appliedFilters: [],
  maxFilters: 20,
  configuration: null,
  isLoadingConfiguration: false,
  configurationError: null,
  disabled: false,
  expanded: true,
  filterMode: FilterEngineMode.FAVORITE,
  selectedCategory: null,
  selectedField: null,
  filterCriteriaButtonTitle: FILTER_CRITERIA_BUTTON_DEFAULT_TITLE,
  currentOpenMenuId: null,
  // Global options
  closeOnClickOutside: false,
  autoApply: true,
  multipleOperatorSupport: false,
  suppressOperator: true
};

export const FilterEngineV2Store = signalStore(
  withState<FilterEngineState>(initialState),

  withComputed((store) => ({
    hasAppliedFilters: computed(() => store.appliedFilters().length > 0),
    appliedFiltersCount: computed(() => store.appliedFilters().length),
    visibleFilters: computed(() => store.appliedFilters().filter((f) => !f.isHidden)),
    visibleFiltersCount: computed(() => store.appliedFilters().filter((f) => !f.isHidden).length),
    isReadonly: computed(() => store.disabled()),
    isFavoriteMode: computed(() => store.filterMode() === FilterEngineMode.FAVORITE),
    isClassicMode: computed(() => store.filterMode() === FilterEngineMode.CLASSIC),
    categories: computed(() => store.configuration()?.categories ?? []),
    hasConfiguration: computed(() => store.configuration() !== null),
    isLoading: computed(() => store.isLoadingConfiguration()),
    hasError: computed(() => store.configurationError() !== null),
    getFiltersForField: computed(() => (fieldKey: string) => {
      const filters = store.appliedFilters();
      return filters.filter((f) => f.criteriaKey === fieldKey);
    })
  })),

  withMethods((store, configurationService = inject(FilterConfigurationService), filterEngineSettingsService = inject(FilterEngineSettingsService)) => ({
    resetState: () => patchState(store, initialState),
    disable: () => patchState(store, { disabled: true }),
    enable: () => patchState(store, { disabled: false }),
    setDisabled: (disabled: boolean) => patchState(store, { disabled }),
    setSuppressOperator: (suppressOperator: boolean) => patchState(store, { suppressOperator }),
    setAppliedFilters: (appliedFilters: Filter[]) => patchState(store, { appliedFilters }),
    setAutoApply: (autoApply: boolean) => patchState(store, { autoApply }),
    setMaxFilters: (maxFilters: number | undefined) => patchState(store, (state) => ({ maxFilters: maxFilters ?? state.maxFilters })),
    addFilter: (filter: Filter) =>
      patchState(store, (state) => ({
        appliedFilters: [...state.appliedFilters, filter]
      })),
    removeFilter: (filterToRemove: Filter) => {
      const filtersAfterRemove = store
        .appliedFilters()
        .filter(
          (f) => !(f.criteriaKey === filterToRemove.criteriaKey && f.value === filterToRemove.value && f.criteriaOperator === filterToRemove.criteriaOperator)
        );
      patchState(store, (state) => ({ appliedFilters: [...filtersAfterRemove] }));
    },
    clearFilters: () => patchState(store, { appliedFilters: [] }),
    setExpanded: (expanded: boolean) => patchState(store, { expanded }),
    toggleExpanded: () => patchState(store, (state) => ({ expanded: !state.expanded })),
    setFilterMode: (filterMode: FilterEngineMode) => patchState(store, { filterMode }),
    toggleFilterMode: () =>
      patchState(store, (state) => ({
        filterMode: state.filterMode === FilterEngineMode.FAVORITE ? FilterEngineMode.CLASSIC : FilterEngineMode.FAVORITE
      })),

    setSelectedCategory: (selectedCategory: FilterCategoryV2 | null) =>
      patchState(store, {
        selectedCategory,
        selectedField: null
      }),
    setSelectedField: (selectedField: FilterFieldV2 | null) => patchState(store, { selectedField }),
    clearSelection: () =>
      patchState(store, {
        selectedCategory: null,
        selectedField: null,
        filterCriteriaButtonTitle: FILTER_CRITERIA_BUTTON_DEFAULT_TITLE
      }),
    setFilterCriteriaButtonTitle: (title: string) => patchState(store, { filterCriteriaButtonTitle: title }),
    resetFilterCriteriaButtonTitle: () => patchState(store, { filterCriteriaButtonTitle: FILTER_CRITERIA_BUTTON_DEFAULT_TITLE }),
    setCloseOnClickOutside: (closeOnClickOutside: boolean) => patchState(store, { closeOnClickOutside }),
    notifyMenuOpened: (menuId: string) => patchState(store, { currentOpenMenuId: menuId }),
    closeAllMenus: () => patchState(store, { currentOpenMenuId: null }),
    isMenuOpen: (menuId: string) => store.currentOpenMenuId() === menuId,

    setMultipleOperatorSupport: (multipleOperatorSupport: boolean) => patchState(store, { multipleOperatorSupport }),

    // Configuration methods (moved from FilterConfigurationService)
    setConfiguration: (configuration: FilterConfigurationV2) =>
      patchState(store, {
        configuration,
        isLoadingConfiguration: false,
        configurationError: null
      }),
    setConfigurationLoading: (isLoadingConfiguration: boolean) => patchState(store, { isLoadingConfiguration }),
    setConfigurationError: (configurationError: string) =>
      patchState(store, {
        configurationError,
        isLoadingConfiguration: false
      }),
    clearConfiguration: () =>
      patchState(store, {
        configuration: null,
        selectedCategory: null,
        isLoadingConfiguration: false,
        configurationError: null
      }),
    loadConfiguration: rxMethod<string>(
      pipe(
        tap(() => patchState(store, { isLoadingConfiguration: true })),
        switchMap((masterView) =>
          configurationService.getConfiguration(masterView).pipe(
            tapResponse({
              next: (categories) =>
                patchState(store, () => ({
                  configuration: configurationService.transformConfiguration(categories, masterView, filterEngineSettingsService.options()),
                  isLoadingConfiguration: false,
                  configurationError: null
                })),
              error: (configurationError: never) =>
                patchState(store, () => ({
                  configuration: { categories: [] },
                  configurationError,
                  isLoadingConfiguration: false
                }))
            })
          )
        )
      )
    )
  })),

  withHooks({
    onDestroy(store) {
      store.resetState();
    }
  })
);
